import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'network/api';
import { useMemo } from 'react';
import {
  BankAccountInfo,
  CommonListParams,
  FarmerGroup,
  DataPagination,
  Stakeholder,
} from 'types/common';
import { cleanObject } from 'utils/helpers';

export interface MachineOwner extends Stakeholder {
  machinesOwned: Partial<MachinesOwned>[];
  bankAccountInfo: BankAccountInfo;
  relatedRizeEmployee: string;
  preferredFarmerGroup: FarmerGroup;
}

export type MachinesOwned = {
  id: string;
  machine: Partial<Machine>;
  quantity: number;
  quantityAvailable: number;
  quantityRented: number;
  quantityBroken: number;
  availability: string;
  pricePerHa: string;
  age: string;
};

export type Machine = {
  id: string;
  type: string;
  brand: string;
  model: string;
};

type MachineOwnerParams = CommonListParams & {
  country?: string;
  agronomist?: string;
  subDistrict?: string;
};

const useMachineOwnerList = (params: MachineOwnerParams, enabled = true) => {
  const { country, search, agronomist, subDistrict, sort, ...restParams } = {
    ...params,
  };
  const queryParams = useMemo(
    () =>
      cleanObject({
        'name.contains': search,
        'country.equals': country,
        'agName.in': agronomist,
        'subDistrict.in': subDistrict,
        ...restParams,
      }),
    [params],
  );

  const query = useQuery<AxiosResponse<DataPagination<MachineOwner>>>({
    queryKey: ['machine-owner', queryParams],
    queryFn: () => API.get('/machine-owner/list', { params: queryParams }),
    enabled: enabled,
    staleTime: 1000 * 10,
  });

  return { ...query };
};

export default useMachineOwnerList;
